// An in-browser stand-in for the Firestore room document, for portals whose security policy
// blocks every outside connection (Playgama) or that allow no third-party SDKs (GamePix, Y8). Quick Play against the computer needs nothing
// from the server: the reducer, the computer players and the timers all run in the browser.
import { isOfflinePortal } from "./portal";

export const isLocalMode = isOfflinePortal || process.env.REACT_APP_LOCAL === "1";

export const LOCAL_UID = "local-player";

type Room = { id: string; owner: string; gameState: any };
type Listener = (room: Room) => void;

const rooms: { [code: string]: Room } = {};
const listeners: { [code: string]: Listener[] } = {};
const rounds: { [gameId: string]: any[] } = {};

const clone = (x: any) => (x === undefined ? x : JSON.parse(JSON.stringify(x)));

// What the game expects in place of a Firestore server timestamp.
export const nowStamp = () => ({ seconds: Math.floor(Date.now() / 1000), nanoseconds: 0 });

export const newId = () => Math.random().toString(36).slice(2, 12) + Math.random().toString(36).slice(2, 12);

const notify = (code: string) => {
  const room = rooms[code];
  if (!room) return;
  // Asynchronously: a listener re-renders the table and may dispatch another update.
  Promise.resolve().then(() => (listeners[code] || []).forEach((l) => l(clone(room))));
};

export const localStore = {
  exists: (code: string) => Boolean(rooms[code]),
  create(code: string, room: Room) {
    rooms[code] = clone(room);
    notify(code);
  },
  get: (code: string): Room | undefined => clone(rooms[code]),
  subscribe(code: string, listener: Listener) {
    (listeners[code] = listeners[code] || []).push(listener);
    if (rooms[code]) Promise.resolve().then(() => listener(clone(rooms[code])));
    return () => {
      listeners[code] = (listeners[code] || []).filter((l) => l !== listener);
    };
  },
  // The equivalent of a Firestore transaction on the room's gameState: rejects if fn throws
  // (an invalid move), and leaves the room untouched if fn returns nothing.
  transact(code: string, fn: (gameState: any) => any): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const room = rooms[code];
        if (!room) return resolve();
        const next = fn(clone(room.gameState));
        if (next) {
          room.gameState = clone(next);
          notify(code);
        }
        resolve();
      } catch (e) {
        reject(e);
      }
    });
  },
  update(code: string, fields: Partial<Room>) {
    const room = rooms[code];
    if (!room) return Promise.resolve();
    Object.assign(room, clone(fields));
    notify(code);
    return Promise.resolve();
  },
  // Finished rounds of a game, for the local scoreboard (the server's saveRound otherwise).
  addRound(gameId: string, round: any) {
    (rounds[gameId] = rounds[gameId] || []).push(clone(round));
    return rounds[gameId].map(clone);
  },
};

// End-to-end tests only: a build made with REACT_APP_E2E=1 lets the test drive the local room
// (e.g. a three-round match against the computers). Every real build leaves the variable unset,
// so webpack drops this.
if (process.env.REACT_APP_E2E === "1" && typeof window !== "undefined") {
  (window as any).__rafikiLocal = localStore;
}
